import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useCreateValidationRule, useUpdateValidationRule } from "@/hooks/useValidationRules";
import { Plus, Save, Code } from "lucide-react";

export function RulesEngineBuilder({ workspaceId }: { workspaceId: string }) {
  const createRule = useCreateValidationRule();
  const updateRule = useUpdateValidationRule();

  const [rule, setRule] = useState({
    rule_name: "",
    rule_type: "field_validation",
    stage: "validation",
    conditions: {},
    severity: "error" as "error" | "warning" | "info",
    is_active: true,
  });

  const handleSave = async () => {
    await createRule.mutateAsync({
      ...rule,
      workspace_id: workspaceId,
    });
    setRule({
      rule_name: "",
      rule_type: "field_validation",
      stage: "validation",
      conditions: {},
      severity: "error",
      is_active: true,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="h-5 w-5" />
          Validation Rules Builder
        </CardTitle>
        <CardDescription>
          Create custom validation rules with advanced logic and conditions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Rule Name</Label>
            <Input
              value={rule.rule_name}
              onChange={(e) => setRule({ ...rule, rule_name: e.target.value })}
              placeholder="e.g., Amount Range Validation"
            />
          </div>

          <div className="space-y-2">
            <Label>Rule Type</Label>
            <Select
              value={rule.rule_type}
              onValueChange={(value) => setRule({ ...rule, rule_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="field_validation">Field Validation</SelectItem>
                <SelectItem value="cross_field">Cross-Field Check</SelectItem>
                <SelectItem value="anomaly_detection">Anomaly Detection</SelectItem>
                <SelectItem value="business_logic">Business Logic</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Condition Logic (JSON)</Label>
          <Textarea
            placeholder='{"field": "amount", "operator": "between", "min": 0, "max": 100000}'
            className="font-mono text-sm"
            rows={6}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Severity</Label>
            <Select
              value={rule.severity}
              onValueChange={(value: any) => setRule({ ...rule, severity: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="error">Error (Block)</SelectItem>
                <SelectItem value="warning">Warning (Flag)</SelectItem>
                <SelectItem value="info">Info (Log)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between pt-8">
            <Label>Active</Label>
            <Switch
              checked={rule.is_active}
              onCheckedChange={(checked) => setRule({ ...rule, is_active: checked })}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={createRule.isPending} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          Save Validation Rule
        </Button>
      </CardContent>
    </Card>
  );
}
